<template>
  <b-modal
    id="create-cff"
    :title="titleText"
    header-class="create-wizard-header"
    title-class="create-label"
    footer-class="create-wizard-footer"
    no-close-on-backdrop
    @shown="init"
    @hidden="clearForm"
  >
    <div class="wizard-container">
      <div class="content-wrapper">
        <div class="wizard-form">
          <div class="ncm-row" v-if="isInputReady">
            <label class="ncm-column-left" for="cff-app-name">
              {{ nameText }}
            </label>
            <div class="ncm-column-right m-name">
              <text-input
                id="cff-app-name"
                v-model="appName"
                type="text"
                :focus="true"
                @on-text-input-change="onAppNameInputChanged"
              />
            </div>
          </div>

          <element-description
            :label="descriptionText"
            :outer-container-class="'ncm-row'"
            :label-class="'ncm-column-left'"
            :value-container-class="'ncm-column-right'"
            :elem-description="appDescription"
            @on-element-description-changed="onDeDescriptionChanged"
          ></element-description>

          <div class="ncm-row">
            <label
              class="ncm-column-left field-label-high"
              :class="{ 'field-label-low': uploadedFile }"
            >
              {{ fileText }}
            </label>
            <div class="ncm-column-right">
              <file-upload @on-file-change="onFileUpload"></file-upload>
            </div>
          </div>

          <div class="ncm-row">
            <label class="ncm-column-left">
              {{ checkboxText }}
            </label>
            <div class="ncm-column-right">
              <b-form-checkbox v-model="shouldSaveTemplate"></b-form-checkbox>
            </div>
          </div>

          <div class="ncm-row" v-if="shouldSaveTemplate">
            <label class="ncm-column-left" for="cff-template-name">
              {{ templateNameText }}
            </label>
            <div class="ncm-column-right m-name">
              <text-input
                id="cff-template-name"
                v-model="templateName"
                type="text"
                @on-text-input-change="onTemplateNameInputChanged"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <button class="modal-btn modal-cancel-btn" @click="clearForm">
        {{ getMessageText(messages.CANCEL) }}
      </button>
      <button
        class="modal-btn modal-save-btn"
        @click="doCreate"
        :disabled="shouldBeDisabled"
      >
        {{ getMessageText(messages.OK) }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import FileUpload from "@/components/FileUpload";
  import TextInput from "@/components/TextInput";
  import templateService from "@/services/templates-service";
  import store from "@/store";
  import { MUTATION_NAME, STATUS_CODE } from "@/static/global-variables";
  import templateServices from "@/services/templates-service";
  import ElementDescription from "@/components/ElementDescription";

  export default {
    name: "NewContentFromFileModal",
    components: {
      TextInput,
      FileUpload,
      ElementDescription
    },
    data() {
      return {
        appName: "",
        templateName: "",
        shouldSaveTemplate: false,
        uploadedFile: null,
        isNewTemplate: false,
        selectedTemplate: null,
        appDescription: null,
        isInputReady: false
      };
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      titleText() {
        return this.$t("newPageFromFile");
      },
      nameText() {
        return this.$t("pageName");
      },
      fileText() {
        return this.$t("file");
      },
      checkboxText() {
        return this.$t("savePageTemplateQuestionMark");
      },
      templateNameText() {
        return this.$t("pageTemplateName");
      },
      shouldBeDisabled() {
        return (
          !this.appName ||
          !this.uploadedFile ||
          (this.shouldSaveTemplate && !this.templateName)
        );
      },
      descriptionText() {
        return this.getMessageText(this.messages.DESCRIPTION);
      }
    },
    methods: {
      init() {
        this.isInputReady = true;
      },
      clearForm() {
        this.appName = "";
        this.appDescription = null;
        this.uploadedFile = null;
        this.shouldSaveTemplate = false;
        this.templateName = "";
        this.isInputReady = false;
        this.$bvModal.hide("create-cff");
      },
      onDeDescriptionChanged(input) {
        this.appDescription = input;
      },
      onFileUpload(val) {
        this.uploadedFile = val.file;
      },
      displayErrorMessage(message) {
        this.$bvToast.toast(message, {
          solid: true,
          toaster: "b-toaster-bottom-center",
          variant: "danger"
        });
      },
      errorHandler(error) {
        const component = this;
        const invalidFormatMsg = component.$t("invalidPageTemplateFileFormat");
        const saveError = component.getMessageText(
          component.messages.SAVE_ERROR
        );
        if (
          error &&
          error.response &&
          error.response.status === STATUS_CODE.BAD_REQUEST
        ) {
          this.displayErrorMessage(invalidFormatMsg);
        } else {
          this.displayErrorMessage(saveError);
        }
      },
      navigateToEditPage(app) {
        this.$router.push({
          name: "DataPalette",
          params: {
            appId: app.uuid,
            appName: app.name,
            type: app.type,
            testRecord: app.testRecord
          }
        });
      },
      doCreate() {
        const name = this.appName;
        const description = this.appDescription;
        if (this.shouldSaveTemplate) {
          templateService
            .createWithFile(this.templateName, null, this.uploadedFile, true)
            .then(resp => {
              if (!resp) {
                this.errorHandler();
                return;
              }

              store.commit(MUTATION_NAME.ADD_APP, resp);
              const params = {
                name,
                description,
                sourceTemplateUuid: resp.uuid,
                sourceTemplateVersion: resp.version,
                isTemplate: false
              };

              templateServices
                .createContentFromTemplate(params)
                .then(resp => {
                  if (!resp) {
                    this.errorHandler();
                    return;
                  }

                  const createdApp = resp;
                  store.commit(MUTATION_NAME.ADD_APP, createdApp);
                  this.navigateToEditPage(createdApp);
                }, this.errorHandler)
                .catch(this.errorHandler);
            }, this.errorHandler)
            .catch(this.errorHandler);
        } else {
          templateService
            .createWithFile(name, description, this.uploadedFile, false)
            .then(resp => {
              if (!resp) {
                this.errorHandler();
                return;
              }
              store.commit(MUTATION_NAME.ADD_APP, resp);
              this.navigateToEditPage(resp);
            }, this.errorHandler)
            .catch(this.errorHandler);
        }
        this.clearForm();
      },
      onAppNameInputChanged(val) {
        this.appName = val;
      },
      onTemplateNameInputChanged(val) {
        this.templateName = val;
      }
    }
  };
</script>

<style scoped>
  .modal-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    padding: 0;
    background: transparent;
    background-clip: border-box;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    line-height: 1.875rem;
    color: #0070d2;
    -webkit-appearance: none;
    white-space: normal;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    text-decoration: none;
  }

  .modal-save-btn {
    margin-left: 0.5rem;
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    background-color: #0070d2;
    border: 1px solid #0070d2;
    color: #fff;
  }

  .modal-cancel-btn {
    padding-left: 1rem;
    padding-right: 1rem;
    text-align: center;
    vertical-align: middle;
    justify-content: center;
    transition: border 0.15s linear;
    border: 1px solid #dddbda;
    background-color: #fff;
  }

  .modal-btn[disabled] {
    background-color: var(--DISABLED_BTN_GREY);
    border-color: var(--DISABLED_BTN_GREY);
    color: var(--WHITE);
  }

  .field-label-high {
    padding-top: 0.75rem;
  }

  .field-label-low {
    padding-top: 0.125rem;
  }
</style>
